import React, { useState, useEffect, useCallback } from 'react';
import { Euro, Save, RotateCcw, Plus, Trash2, Calculator, Globe2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import api from '../../lib/api';

const ZONE_LABEL = { W: 'Ouest', S: 'Sud', E: 'Est', N: 'Nord', SE: 'Sud-Est' };
const regionName = (code) => {
  try { return new Intl.DisplayNames(['fr'], { type: 'region' }).of(code) || code; } catch { return code; }
};

const PriceInput = ({ value, onChange, testId }) => (
  <div className="relative">
    <Input type="number" step="0.01" min="0" value={value ?? ''} onChange={(e) => onChange(e.target.value)}
      className="h-8 text-xs pr-6 w-24" data-testid={testId} />
    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-[#5A8BA8]">€</span>
  </div>
);

const CountrySelect = ({ value, onChange, countries, testId }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}
    className="h-8 text-xs border border-[#D6E8F0] rounded-md px-2 bg-white">
    {countries.map(c => <option key={c} value={c}>{regionName(c)} ({c})</option>)}
  </select>
);

const Section = ({ title, hint, children, testId }) => (
  <div className="bg-white rounded-xl border border-[#E8F4FD] p-4 mb-4" data-testid={testId}>
    <h4 className="font-bold text-[#033871] text-sm">{title}</h4>
    {hint && <p className="text-[11px] text-[#5A8BA8] mb-3">{hint}</p>}
    {children}
  </div>
);

const Simulator = ({ countries }) => {
  const [from, setFrom] = useState('IT');
  const [to, setTo] = useState('FR');
  const [result, setResult] = useState(null);
  const run = async () => {
    try {
      const res = await api.get('/admin/eurosender-grid/quote', { params: { from_country: from, to_country: to } });
      setResult(res.data.option);
    } catch { toast.error('Erreur de simulation'); }
  };
  return (
    <Section title="Simulateur" hint="Prix payé par l'acheteur selon la grille enregistrée (marge incluse)." testId="eurosender-simulator">
      <div className="flex flex-wrap items-center gap-2">
        <CountrySelect value={from} onChange={setFrom} countries={countries.filter(c => c !== 'FR')} testId="sim-from" />
        <span className="text-xs text-[#5A8BA8]">→</span>
        <CountrySelect value={to} onChange={setTo} countries={countries} testId="sim-to" />
        <Button size="sm" onClick={run} className="h-8 text-xs bg-[#0C63BF]" data-testid="sim-run">
          <Calculator className="w-3.5 h-3.5 mr-1" /> Calculer
        </Button>
        {result && (
          <span className="text-sm font-bold text-[#033871]" data-testid="sim-result">
            {result.price.toFixed(2)} € <span className="text-[11px] font-normal text-[#5A8BA8]">• {result.delay}</span>
          </span>
        )}
      </div>
    </Section>
  );
};

const DomesticSection = ({ grid, zones, setField }) => (
  <Section title="Prix nationaux et zones par pays" hint="Prix d'un envoi à l'intérieur du pays (coût Eurosender, hors marge). La zone sert au calcul des envois internationaux." testId="eurosender-domestic">
    <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2">
      {Object.keys(grid.zone_of).sort().map(c => (
        <div key={c} className="flex items-center justify-between gap-2 text-xs">
          <span className="text-[#033871] flex-1 truncate">{regionName(c)} ({c})</span>
          {c === 'FR'
            ? <span className="text-[10px] text-[#5A8BA8] w-24">Packlink</span>
            : <PriceInput value={grid.domestic[c]} onChange={(v) => setField('domestic', c, v)} testId={`domestic-${c}`} />}
          <select value={grid.zone_of[c]} onChange={(e) => setField('zone_of', c, e.target.value)} data-testid={`zone-${c}`}
            className="h-8 text-xs border border-[#D6E8F0] rounded-md px-1 bg-white w-24">
            {zones.map(z => <option key={z} value={z}>{ZONE_LABEL[z]}</option>)}
          </select>
        </div>
      ))}
    </div>
  </Section>
);

const ZoneSection = ({ grid, setField }) => (
  <Section title="Prix entre zones" hint="Utilisé quand aucun trajet précis n'est défini (coût hors marge)." testId="eurosender-zones">
    <div className="grid sm:grid-cols-3 gap-2">
      {Object.keys(grid.zone_prices).map(k => {
        const [a, b] = k.split('-');
        return (
          <div key={k} className="flex items-center justify-between gap-2 text-xs">
            <span className="text-[#033871]">{ZONE_LABEL[a]} ↔ {ZONE_LABEL[b]}</span>
            <PriceInput value={grid.zone_prices[k]} onChange={(v) => setField('zone_prices', k, v)} testId={`zoneprice-${k}`} />
          </div>
        );
      })}
    </div>
  </Section>
);

const RoutesSection = ({ grid, countries, setField, removeRoute }) => {
  const [from, setFrom] = useState('ES');
  const [to, setTo] = useState('FR');
  const add = () => {
    if (from === to) { toast.error('Choisissez deux pays différents'); return; }
    setField('routes', `${from}-${to}`, grid.routes[`${from}-${to}`] ?? 15);
  };
  return (
    <Section title="Trajets précis" hint="Prioritaires sur les zones (coût hors marge, dans un seul sens)." testId="eurosender-routes">
      <div className="space-y-2 mb-3">
        {Object.keys(grid.routes).sort().map(k => {
          const [a, b] = k.split('-');
          return (
            <div key={k} className="flex items-center gap-2 text-xs" data-testid={`route-row-${k}`}>
              <span className="text-[#033871] flex-1">{regionName(a)} → {regionName(b)}</span>
              <PriceInput value={grid.routes[k]} onChange={(v) => setField('routes', k, v)} testId={`route-${k}`} />
              <button onClick={() => removeRoute(k)} className="text-red-500 hover:text-red-700" data-testid={`route-delete-${k}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          );
        })}
      </div>
      <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-[#F0F7FA]">
        <CountrySelect value={from} onChange={setFrom} countries={countries.filter(c => c !== 'FR')} testId="route-add-from" />
        <span className="text-xs">→</span>
        <CountrySelect value={to} onChange={setTo} countries={countries} testId="route-add-to" />
        <Button size="sm" variant="outline" onClick={add} className="h-8 text-xs" data-testid="route-add-btn">
          <Plus className="w-3.5 h-3.5 mr-1" /> Ajouter
        </Button>
      </div>
    </Section>
  );
};

const AdminEurosenderTab = () => {
  const [grid, setGrid] = useState(null);
  const [zones, setZones] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/admin/eurosender-grid');
      setGrid(res.data.grid);
      setZones(res.data.zones);
    } catch { toast.error('Erreur lors du chargement de la grille'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setField = (section, key, value) => setGrid(g => ({ ...g, [section]: { ...g[section], [key]: value } }));
  const setTop = (key, value) => setGrid(g => ({ ...g, [key]: value }));
  const removeRoute = (key) => setGrid(g => { const r = { ...g.routes }; delete r[key]; return { ...g, routes: r }; });

  const toNumbers = (obj) => Object.fromEntries(Object.entries(obj).map(([k, v]) => [k, parseFloat(v)]));
  const save = async () => {
    const payload = {
      ...grid,
      margin: parseFloat(grid.margin), neighbour_price: parseFloat(grid.neighbour_price), island_surcharge: parseFloat(grid.island_surcharge),
      domestic: toNumbers(grid.domestic), zone_prices: toNumbers(grid.zone_prices), routes: toNumbers(grid.routes),
    };
    const all = [payload.margin, payload.neighbour_price, payload.island_surcharge, ...Object.values(payload.domestic), ...Object.values(payload.zone_prices), ...Object.values(payload.routes)];
    if (all.some(n => Number.isNaN(n) || n < 0)) { toast.error('Un prix est vide ou invalide'); return; }
    setSaving(true);
    try {
      const res = await api.put('/admin/eurosender-grid', payload);
      setGrid(res.data.grid);
      toast.success('Grille Eurosender enregistrée');
    } catch (e) { toast.error(e.response?.data?.detail?.[0]?.msg || e.response?.data?.detail || "Erreur d'enregistrement"); }
    finally { setSaving(false); }
  };
  const reset = async () => {
    if (!window.confirm("Revenir aux prix Eurosender d'origine (juin 2026) ? Vos modifications seront perdues.")) return;
    try {
      const res = await api.post('/admin/eurosender-grid/reset');
      setGrid(res.data.grid);
      toast.success('Grille réinitialisée');
    } catch { toast.error('Erreur de réinitialisation'); }
  };

  if (!grid) return <p className="text-center text-sm text-[#5A8BA8] py-6">Chargement…</p>;
  const countries = Object.keys(grid.zone_of).sort();

  return (
    <div data-testid="admin-eurosender">
      <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-xl flex flex-wrap items-start justify-between gap-3">
        <div className="max-w-xl">
          <h3 className="font-bold text-[#033871] flex items-center gap-2 mb-1"><Globe2 className="w-4 h-4" /> Grille Eurosender</h3>
          <p className="text-xs text-[#5A8BA8]">Prix appliqués aux vendeurs hors France (colis ≤ 2 kg). Prix acheteur = coût + marge.</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={reset} className="h-9 text-xs" data-testid="eurosender-reset-btn">
            <RotateCcw className="w-3.5 h-3.5 mr-1" /> Réinitialiser
          </Button>
          <Button size="sm" onClick={save} disabled={saving} className="h-9 text-xs bg-[#32CD32] hover:bg-[#28a428]" data-testid="eurosender-save-btn">
            <Save className="w-3.5 h-3.5 mr-1" /> {saving ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
        </div>
      </div>

      <Section title="Réglages généraux" testId="eurosender-general">
        <div className="flex flex-wrap gap-6 text-xs">
          <label className="flex items-center gap-2"><Euro className="w-3.5 h-3.5 text-[#0C63BF]" /> Marge par envoi
            <PriceInput value={grid.margin} onChange={(v) => setTop('margin', v)} testId="eurosender-margin" /></label>
          <label className="flex items-center gap-2">Pays voisins (ES↔PT, BE↔NL…)
            <PriceInput value={grid.neighbour_price} onChange={(v) => setTop('neighbour_price', v)} testId="eurosender-neighbour" /></label>
          <label className="flex items-center gap-2">Supplément îles (Chypre, Malte)
            <PriceInput value={grid.island_surcharge} onChange={(v) => setTop('island_surcharge', v)} testId="eurosender-island" /></label>
        </div>
      </Section>

      <Simulator countries={countries} />
      <DomesticSection grid={grid} zones={zones} setField={setField} />
      <ZoneSection grid={grid} setField={setField} />
      <RoutesSection grid={grid} countries={countries} setField={setField} removeRoute={removeRoute} />
    </div>
  );
};

export default AdminEurosenderTab;
